/**
 * ToolCallPartRenderer Component - Reactive Version
 *
 * Uses the reactive GraphStore pattern:
 * - useNode(part.response) for reactive access to tool call response
 * - No manual DAG reconstruction needed
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, Wrench, CheckCircle, XCircle } from 'lucide-react';
import { usePartDeltaStreaming } from '@sourcepilot/ui/framework/data/dag-sync/hooks';
import { ToolCallPartReadAPISchema } from '@sourcepilot/generated/chat/types/toolcallpart';
import { z } from 'zod';
import { useNode } from '@sourcepilot/framework/relationship-manager';
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext';
import { AutoApprovedLabel, isAutoApprovedInProposal } from './approval-card';

export enum StreamMode {
    StreamObject = 'stream_object',
    StreamObjectKeyValueTokens = 'stream_object_key_value_tokens',
}

export class JsonCloser {
    private mode: StreamMode;

    private buffer = "";
    private stack: any[] = [];

    private prevSize = 0;

    private closedObject = false;
    private closedArray = false;

    constructor(mode: StreamMode = StreamMode.StreamObject) {
        this.mode = mode;
    }

    append(chunk: string) {
        for (const char of chunk) {
            this.buffer += char;

            switch (char) {
                case "{":
                    this.stack.push(char);
                    this.closedObject = false;
                    break;
                case "}":
                    if (this.stack[this.stack.length - 1] === "{") {
                        this.stack.pop();
                    }
                    this.closedObject = true;
                    break;
                case "[":
                    this.stack.push(char);
                    this.closedArray = false;
                    break;
                case "]":
                    if (this.stack[this.stack.length - 1] === "[") {
                        this.stack.pop();
                    }
                    this.closedArray = true;
                    break;
                case '"':
                    if (this.stack[this.stack.length - 1] === '"') {
                        this.stack.pop();
                    } else {
                        this.stack.push(char);
                    }
                    break;
                default:
                    break;
            }
        }
    }

    closeJson(): string {
        let closeBuffer = this.buffer.trim();

        for (const char of [...this.stack].reverse()) {
            switch (char) {
                case "{":
                    if (closeBuffer[closeBuffer.length - 1] === ",") {
                        closeBuffer = closeBuffer.slice(0, -1);
                    }
                    closeBuffer += "}";
                    break;
                case "[":
                    if (closeBuffer[closeBuffer.length - 1] === ",") {
                        closeBuffer = closeBuffer.slice(0, -1);
                    }
                    closeBuffer += "]";
                    break;
                case '"':
                    if (this.mode === StreamMode.StreamObjectKeyValueTokens) {
                        closeBuffer += '"';
                    }
                    break;
                default:
                    break;
            }
        }

        return closeBuffer;
    }

    parse(): [boolean, any, any[]] {
        try {
            const closedJson = this.closeJson();
            const jsonRes = JSON.parse(closedJson);

            const size = JSON.stringify(jsonRes).length;

            let hasChanged = false;
            if (size > this.prevSize) {
                this.prevSize = size;
                hasChanged = true;
            }
            // Do not process twice if the array and the object get closed.
            // XOR operation to check if either the array or object get closed but not both
            else if (this.closedObject !== this.closedArray) {
                // If the object have been closed consider it as a change
                // If the array have been close the object have been closed too
                // No need to consider it as a change
                // This is to avoid processing twice the same completion
                hasChanged = this.closedObject;
                this.closedObject = false;
            }

            return [hasChanged, jsonRes, this.stack];
        } catch (error) {
            return [false, null, this.stack];
        }
    }
}

interface ToolCallPartRendererProps {
  part: z.infer<typeof ToolCallPartReadAPISchema>; // Resolved ToolCallPart data object
  renderConfig: {
    showAllToolCalls?: boolean;
    showSysPrompts?: boolean;
  };
  isStreaming: boolean;
}

export function ToolCallPartRenderer({
  part,
  renderConfig,
  isStreaming
}: ToolCallPartRendererProps) {
  const [isExpanded, setIsExpanded] = useState(renderConfig.showAllToolCalls || false);
  const { SpawnedTaskComponent } = useAppHost();

  // Helper function to pretty-print JSON strings
  const prettyPrintJson = useCallback((value: any): string => {
    if (typeof value === 'string') {
      // Try to parse if it looks like escaped JSON
      try {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      } catch {
        // If parsing fails, check if it's double-escaped
        try {
          const unescaped = value.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
          const parsed = JSON.parse(unescaped);
          return JSON.stringify(parsed, null, 2);
        } catch {
          // If still fails, return as-is but cleaned up
          return value.replace(/^"/, '').replace(/"$/, '').replace(/\\"/g, '"').replace(/\\n/g, '\n');
        }
      }
    }
    return JSON.stringify(value, null, 2);
  }, []);

  const args = part.args || {};
  const toolName = part.tool_name || '';
  const toolCallId = part.tool_call_id;
  const jsonParserRef = useRef<JsonCloser | null>(null);
  if (!jsonParserRef.current) {
    jsonParserRef.current = new JsonCloser(StreamMode.StreamObjectKeyValueTokens);
  }
  const closer = jsonParserRef.current;

  const [streamingArgs, setStreamingArgs] = useState("");
  const [streamingToolName, setStreamingToolName] = useState("");

  // Memoize the callback to prevent infinite re-subscriptions
  const handleDelta = useCallback((delta: any) => {
    // Handle delta immediately when it arrives
    const delta_data = delta.delta?.delta_data;
    const delta_type = delta.delta?.delta_type;
    const args_json_delta = delta_data?.args_delta?.args_json_delta;

    if (args_json_delta && delta_type === 'tool_call_building') {
      closer.append(args_json_delta);
      const [hasChanged, streamingArgsObject] = closer.parse();
      if (hasChanged) {
        setStreamingArgs(JSON.stringify(streamingArgsObject, null, 2));
      }
    }

    if (delta_data?.tool_name_delta && delta_type === 'tool_call_building') {
      setStreamingToolName(prev => prev + delta_data.tool_name_delta);
    }
  }, [closer]);

  // Subscribe to streaming deltas for tool call updates with callback
  const { deltaCount } = usePartDeltaStreaming(
    part.is_streaming ? (part.id || null) : null,
    0, // startSequence
    handleDelta
  );

  // Reset streaming states when part changes (non-streaming parts)
  useEffect(() => {
    if (!part.is_streaming) {
      setStreamingArgs("");
      setStreamingToolName("");
    }
  }, [part.tool_name, part.args, part.is_streaming]);

  // For streaming, use parsed streamingArgs from JSONParser, otherwise use original args
  // Determine what to display for arguments - simplified
  const argsToDisplay = useMemo(() => {
    if (isStreaming) {
      return streamingArgs;
    }
    return prettyPrintJson(args);
  }, [args, prettyPrintJson, streamingArgs, isStreaming]);

  const toolNameToDisplay = useMemo(() => {
    if (isStreaming && streamingToolName) {
      return streamingToolName;
    }
    return toolName;
  }, [isStreaming, toolName, streamingToolName]);

  return (
    <div className="border border-gray-200 rounded-md mb-2 bg-gray-50">
      {/* Tool call header - always visible */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <Wrench className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-sm">
            {part.descriptive_name || toolNameToDisplay}
          </span>
          {/* Show different states based on tool call lifecycle */}
          {part.is_streaming && (
            <span className="text-xs text-orange-500 animate-pulse">executing...</span>
          )}
          {isAutoApprovedInProposal((part as any).pending_approval_preview) && (
            <AutoApprovedLabel proposal={(part as any).pending_approval_preview.proposal} />
          )}
        </div>
        <div className="flex items-center space-x-2">
          {toolCallId && (
            <span className="text-xs text-gray-400 font-mono">
              {toolCallId.substring(0, 8)}...
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-3 space-y-3">
          <>
            {part.args?.explanation && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <p className="text-xs text-blue-900">{String(part.args.explanation)}</p>
              </div>
            )}
          </>

          {/* Arguments */}
          <div>
            <h4 className="text-xs font-semibold text-gray-600 mb-1">
              Arguments:
              {(deltaCount > 0) && (
                <span className="ml-2 text-orange-500 animate-pulse">(streaming... {deltaCount} deltas)</span>
              )}
            </h4>
            <pre className="text-xs bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap">
              <code>{argsToDisplay}</code>
            </pre>
          </div>

          {/* Response - reactive access */}
          <ToolCallResponse part={part} prettyPrintJson={prettyPrintJson} />

          {/* Spawned sub-task - reactive access; component supplied by host app */}
          <SpawnedTaskComponent part={part} />
        </div>
      )}
    </div>
  );
}

/**
 * Helper component to reactively access tool call response
 */
function ToolCallResponse({
  part,
  prettyPrintJson
}: {
  part: any;
  prettyPrintJson: (value: any) => string;
}) {
  // REACTIVE: Get tool call response from GraphStore using standalone hook
  const response = useNode(part.response) as any;

  if (!response && !part.is_streaming) {
    return (
      <div className="text-xs text-gray-500 italic">
        Awaiting tool execution...
      </div>
    );
  }

  if (!response && part.is_streaming) {
    return (
      <div className="text-xs text-orange-500 italic animate-pulse">
        Tool executing...
      </div>
    );
  }

  if (!response) {
    return null;
  }

  const hasError = response.is_error ?? false;

  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-600 mb-1">
        {hasError ? 'Error:' : 'Result:'}
      </h4>
      <div className={`text-xs p-2 rounded border ${
        hasError ? 'bg-red-50 text-red-700' : 'bg-white'
      }`}>
        {hasError ? (
          <span>{response.tool_error_message || 'Tool call failed'}</span>
        ) : (
          <pre className="overflow-x-auto whitespace-pre-wrap">
            <code>{prettyPrintJson(response.content)}</code>
          </pre>
        )}
      </div>
    </div>
  );
}

